import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/home';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryArticles = slug ? getArticlesByCategory(slug) : [];

  useEffect(() => {
    if (currentCategory) {
      document.title = `${currentCategory.name} - 福利视频网站免费高清资源`;
    }
  }, [currentCategory]);

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center bg-background-50 pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-800 mb-2">分类不存在</h2>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />
          </div>

          <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100">
                <i className={`${currentCategory.icon} text-xl text-primary-500`}></i>
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-0.5">
                  {currentCategory.description}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-6 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-100 rounded-xl overflow-hidden cursor-pointer transition-all hover:-translate-y-1"
                  >
                    <div className="relative w-full aspect-video overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-background-950/70 text-background-50">
                          {article.duration}
                        </span>
                      </div>
                      <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded-md bg-background-950/70 text-background-50 text-xs">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-star-fill text-accent-500 text-xs"></i>
                        </div>
                        {article.rating}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-2 line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 mb-3">
                        {article.description}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span>{article.updateTime}</span>
                        <span>{(article.viewCount / 10000).toFixed(1)} 万次观看</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                  <i className="ri-inbox-line text-3xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500">暂无该分类的视频资源</p>
              </div>
            )}
          </section>
        </main>
        <Footer />
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory.name} - 福利视频网站`,
            description: currentCategory.description,
            url: `https://fulishipin.com/category/${currentCategory.slug}`,
            mainEntity: {
              '@type': 'ItemList',
              itemListElement: categoryArticles.map((article, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                item: {
                  '@type': 'Movie',
                  name: article.title,
                  url: `https://fulishipin.com/article/${article.slug}`,
                  image: article.image,
                  datePublished: article.updateTime,
                },
              })),
            },
          }),
        }}
      />
    </>
  );
}